import { useState, useEffect, useId } from "react";
import { useRegionAndLocationContext } from "../../../contexts/RegionAndLocationContext";
import { autoFormatDateInput, parseDateInput, formatStoredDateForDisplay } from "../../../utils/dateInputUtils";
import { formatPhoneNumber, stripNonDigits } from "../../../utils/phoneFormat";
import { ImageUpload } from "../../ui";
import "../../../css/UserProfileTabs.css";

interface PersonalInfoTabProps {
    user: any; // User from API
    sensitiveInfo: any; // SensitiveUserInfo from API
    isEditing: boolean;
    readOnly: boolean;
    onFieldChange?: (field: string, oldValue: string, newValue: string) => void;
    photoPreviewUrl?: string;
    onPhotoSelect?: (file: File) => void;
    isUploadingPhoto?: boolean;
}

interface FormState {
    name: string;
    email: string;
    contactPhoneNumber: string;
    dateOfBirth: string;
    emergencyContactName: string;
    emergencyContactRelationship: string;
    emergencyContactPhoneNumber: string;
    driversLicenseNumber: string;
    driversLicenseState: string;
    driversLicenseClass: string;
    driversLicenseExpirationDate: string;
    role: string;
    currentJobClassification: string;
    dateOfHire: string;
    discrepancyNotification: boolean;
    pmNotification: boolean;
    trainingNotification: boolean;
}

const normalizePhone = (value?: string | null): string => stripNonDigits(value || "");

const getInitials = (name?: string | null): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (parts[0][0] || '?').toUpperCase();
};

const getInitialFormState = (user: any, sensitiveInfo: any): FormState => ({
    name: user?.name || "",
    email: user?.email || "",
    contactPhoneNumber: normalizePhone(user?.contactPhoneNumber),
    dateOfBirth: sensitiveInfo?.dateOfBirth || "",
    emergencyContactName: sensitiveInfo?.emergencyContactName || "",
    emergencyContactRelationship: sensitiveInfo?.emergencyContactRelationship || "",
    emergencyContactPhoneNumber: normalizePhone(sensitiveInfo?.emergencyContactPhoneNumber),
    driversLicenseNumber: sensitiveInfo?.driversLicenseNumber || "",
    driversLicenseState: sensitiveInfo?.driversLicenseState || "",
    driversLicenseClass: sensitiveInfo?.driversLicenseClass || "",
    driversLicenseExpirationDate: sensitiveInfo?.driversLicenseExpirationDate || "",
    role: user?.role || "",
    currentJobClassification: user?.currentJobClassification || "",
    dateOfHire: user?.dateOfHire || "",
    discrepancyNotification: user?.discrepancyNotification || false,
    pmNotification: user?.pmNotification || false,
    trainingNotification: user?.trainingNotification || false,
});

const formatRoleName = (role?: string | null): string => {
    if (!role) return "—";
    return role
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
};

export const PersonalInfoTab = ({
    user,
    sensitiveInfo,
    isEditing,
    readOnly,
    onFieldChange,
    photoPreviewUrl,
    onPhotoSelect,
    isUploadingPhoto = false,
}: PersonalInfoTabProps) => {
    const profilePhotoInputId = useId();
    const profilePhotoLabelId = useId();
    const [form, setForm] = useState<FormState>(getInitialFormState(user, sensitiveInfo));
    const [dateTexts, setDateTexts] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isEditing) {
            return;
        }

        setForm(getInitialFormState(user, sensitiveInfo));
        // Initialize date display texts from stored values
        const initial = getInitialFormState(user, sensitiveInfo);
        const texts: Record<string, string> = {};
        const dateFields: (keyof FormState)[] = ['dateOfBirth', 'driversLicenseExpirationDate', 'dateOfHire'];
        for (const field of dateFields) {
            const stored = initial[field];
            if (typeof stored === 'string' && stored) {
                const display = formatStoredDateForDisplay(stored);
                texts[field] = display === '-' ? '' : display;
            } else {
                texts[field] = '';
            }
        }
        setDateTexts(texts);
    }, [user, sensitiveInfo, isEditing]);

    const handleChange = (field: keyof FormState, value: string | boolean) => {
        const oldValue = String(form[field] ?? "");
        const newValue = String(value);
        setForm((prev) => ({ ...prev, [field]: value }));
        if (onFieldChange && oldValue !== newValue) {
            onFieldChange(field, oldValue, newValue);
        }
    };

    const handlePhoneChange = (field: "contactPhoneNumber" | "emergencyContactPhoneNumber", input: string) => {
        handleChange(field, stripNonDigits(input).slice(0, 10));
    };

    const renderProfilePhotoField = () => {
        if (isEditing && !readOnly && onPhotoSelect) {
            return (
                <div className="upt-field-row">
                    <label id={profilePhotoLabelId} htmlFor={profilePhotoInputId} className="upt-field-label">
                        Profile Photo
                    </label>
                    <ImageUpload
                        inputId={profilePhotoInputId}
                        labelId={profilePhotoLabelId}
                        previewUrl={photoPreviewUrl}
                        previewAlt={user?.name ? `${user.name} profile photo preview` : 'Profile photo preview'}
                        fallbackInitials={getInitials(user?.name)}
                        inputAriaLabel={
                            photoPreviewUrl ? 'Change profile photo' : 'Upload profile photo'
                        }
                        onSelect={onPhotoSelect}
                        isUploading={isUploadingPhoto}
                    />
                </div>
            );
        }

        return (
            <div className="upt-field-row">
                <span className="upt-field-label">Profile Photo</span>
                {photoPreviewUrl ? (
                    <div className="image-upload-preview">
                        <img
                            src={photoPreviewUrl}
                            alt={user?.name ? `${user.name} profile photo` : 'Profile photo'}
                            onError={(e) => {
                                e.currentTarget.src = '/image-not-found.jpeg';
                            }}
                        />
                    </div>
                ) : (
                    <span className="upt-field-value empty">Not provided</span>
                )}
            </div>
        );
    };

    const renderField = (
        label: string,
        field: keyof FormState,
        options?: { type?: string; placeholder?: string },
    ) => {
        const value = form[field];
        const displayValue = typeof value === "boolean" ? (value ? "Enabled" : "Disabled") : (value || "");
        const isDateField = options?.type === "date";
        const isPhoneField = options?.type === "phone";

        if (isEditing && !readOnly) {
            if (isDateField) {
                const textValue = dateTexts[field] || "";
                return (
                    <div className="upt-field-row">
                        <span className="upt-field-label">{label}</span>
                        <input
                            className="upt-field-input"
                            type="text"
                            value={textValue}
                            placeholder="MM/DD/YYYY"
                            onChange={(e) => {
                                const formatted = autoFormatDateInput(e.target.value, textValue);
                                setDateTexts((prev) => ({ ...prev, [field]: formatted }));
                                const parsed = parseDateInput(formatted);
                                if (parsed) {
                                    handleChange(field, parsed);
                                }
                            }}
                        />
                    </div>
                );
            }
            if (isPhoneField) {
                const rawPhone = typeof value === "string" ? value : "";
                return (
                    <div className="upt-field-row">
                        <span className="upt-field-label">{label}</span>
                        <input
                            className="upt-field-input"
                            type="tel"
                            inputMode="numeric"
                            value={formatPhoneNumber(rawPhone)}
                            placeholder={options?.placeholder || "(xxx) xxx-xxxx"}
                            onChange={(e) =>
                                handlePhoneChange(
                                    field as "contactPhoneNumber" | "emergencyContactPhoneNumber",
                                    e.target.value,
                                )
                            }
                        />
                    </div>
                );
            }
            return (
                <div className="upt-field-row">
                    <span className="upt-field-label">{label}</span>
                    <input
                        className="upt-field-input"
                        type={options?.type || "text"}
                        value={typeof value === "boolean" ? "" : (value || "")}
                        placeholder={options?.placeholder || `Enter ${label.toLowerCase()}`}
                        onChange={(e) => handleChange(field, e.target.value)}
                    />
                </div>
            );
        }

        // Display mode: show date fields formatted
        if (isDateField && typeof value === "string" && value) {
            const formatted = formatStoredDateForDisplay(value);
            return (
                <div className="upt-field-row">
                    <span className="upt-field-label">{label}</span>
                    <span className="upt-field-value">{formatted === "-" ? "Not provided" : formatted}</span>
                </div>
            );
        }

        if (isPhoneField && typeof value === "string" && value) {
            return (
                <div className="upt-field-row">
                    <span className="upt-field-label">{label}</span>
                    <span className="upt-field-value">{formatPhoneNumber(value)}</span>
                </div>
            );
        }

        return (
            <div className="upt-field-row">
                <span className="upt-field-label">{label}</span>
                <span className={`upt-field-value ${!displayValue ? "empty" : ""}`}>
                    {displayValue || "Not provided"}
                </span>
            </div>
        );
    };

    const { companyLocations } = useRegionAndLocationContext();

    const equipmentTypes = (() => {
        return user?.currentJobClassification || "—";
    })();

    const currentLocation = (() => {
        const locationIds = user?.locationAssignment;
        if (Array.isArray(locationIds) && locationIds.length > 0) {
            const uniqueIds = [...new Set(locationIds.filter(Boolean))];
            const names = uniqueIds
                .map((id: string) => {
                    const loc = companyLocations.find((l) => l.id === id);
                    return loc?.name || null;
                })
                .filter(Boolean);
            return names.length > 0 ? names.join(", ") : "—";
        }
        return "—";
    })();

    return (
        <div className="upt-tab-container">
            {/* Left Column — Personal Information */}
            <div className="upt-section">
                <h3 className="upt-section-title">Personal Information</h3>
                <div className="upt-field-list">
                    {renderField("Full Name", "name")}
                    {renderField("Phone", "contactPhoneNumber", { type: "phone", placeholder: "(xxx) xxx-xxxx" })}
                    {renderField("Date of Birth", "dateOfBirth", { type: "date" })}
                    {renderProfilePhotoField()}

                    {/* Emergency Contact */}
                    <div className="upt-field-row">
                        <span className="upt-field-label">Emergency Contact</span>
                        {isEditing && !readOnly ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                <input
                                    className="upt-field-input"
                                    type="text"
                                    value={form.emergencyContactName}
                                    placeholder="Contact name"
                                    onChange={(e) => handleChange("emergencyContactName", e.target.value)}
                                />
                                <input
                                    className="upt-field-input"
                                    type="text"
                                    value={form.emergencyContactRelationship}
                                    placeholder="Relationship"
                                    onChange={(e) => handleChange("emergencyContactRelationship", e.target.value)}
                                />
                                <input
                                    className="upt-field-input"
                                    type="tel"
                                    inputMode="numeric"
                                    value={formatPhoneNumber(form.emergencyContactPhoneNumber)}
                                    placeholder="(xxx) xxx-xxxx"
                                    onChange={(e) => handlePhoneChange("emergencyContactPhoneNumber", e.target.value)}
                                />
                            </div>
                        ) : (
                            <span className={`upt-field-value ${!form.emergencyContactName ? "empty" : ""}`}>
                                {form.emergencyContactName
                                    ? `${form.emergencyContactName}${form.emergencyContactRelationship ? ` (${form.emergencyContactRelationship})` : ""} — ${form.emergencyContactPhoneNumber ? formatPhoneNumber(form.emergencyContactPhoneNumber) : "No phone"}`
                                    : "Not provided"}
                            </span>
                        )}
                    </div>

                    {/* Driver's License */}
                    <div className="upt-field-row">
                        <span className="upt-field-label">Driver's License</span>
                        {isEditing && !readOnly ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                <input
                                    className="upt-field-input"
                                    type="text"
                                    value={form.driversLicenseNumber}
                                    placeholder="License number"
                                    onChange={(e) => handleChange("driversLicenseNumber", e.target.value)}
                                />
                                <div style={{ display: "flex", gap: "8px" }}>
                                    <input
                                        className="upt-field-input"
                                        type="text"
                                        value={form.driversLicenseState}
                                        placeholder="State"
                                        onChange={(e) => handleChange("driversLicenseState", e.target.value)}
                                        style={{ flex: 1 }}
                                    />
                                    <input
                                        className="upt-field-input"
                                        type="text"
                                        value={form.driversLicenseClass}
                                        placeholder="Class"
                                        onChange={(e) => handleChange("driversLicenseClass", e.target.value)}
                                        style={{ flex: 1 }}
                                    />
                                </div>
                                <input
                                    className="upt-field-input"
                                    type="text"
                                    value={dateTexts["driversLicenseExpirationDate"] || ""}
                                    placeholder="MM/DD/YYYY"
                                    onChange={(e) => {
                                        const prev = dateTexts["driversLicenseExpirationDate"] || "";
                                        const formatted = autoFormatDateInput(e.target.value, prev);
                                        setDateTexts((p) => ({ ...p, driversLicenseExpirationDate: formatted }));
                                        const parsed = parseDateInput(formatted);
                                        if (parsed) {
                                            handleChange("driversLicenseExpirationDate", parsed);
                                        }
                                    }}
                                />
                            </div>
                        ) : (
                            <span className={`upt-field-value ${!form.driversLicenseNumber ? "empty" : ""}`}>
                                {form.driversLicenseNumber
                                    ? `${form.driversLicenseNumber} — ${form.driversLicenseState || "?"} Class ${form.driversLicenseClass || "?"} (Exp: ${form.driversLicenseExpirationDate || "N/A"})`
                                    : "Not provided"}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Right Column — Account */}
            <div className="upt-section">
                <h3 className="upt-section-title">Account</h3>
                <div className="upt-field-list">
                    {/* Role — display only */}
                    <div className="upt-field-row">
                        <span className="upt-field-label">Role</span>
                        <span className="upt-field-value">{formatRoleName(user?.role)}</span>
                    </div>

                    {/* Email */}
                    <div className="upt-field-row">
                        <span className="upt-field-label">Email</span>
                        <span className="upt-field-value">{user?.email || "—"}</span>
                    </div>

                    {/* Equipment Types */}
                    <div className="upt-field-row">
                        <span className="upt-field-label">Equipment Types</span>
                        <span className={`upt-field-value ${equipmentTypes === "—" ? "empty" : ""}`}>
                            {equipmentTypes}
                        </span>
                    </div>

                    {/* Current Location */}
                    <div className="upt-field-row">
                        <span className="upt-field-label">Current Location</span>
                        <span className={`upt-field-value ${currentLocation === "—" ? "empty" : ""}`}>
                            {currentLocation}
                        </span>
                    </div>

                    {/* Hire Date */}
                    {renderField("Hire Date", "dateOfHire", { type: "date" })}

                    {/* Notification Preferences */}
                    <div className="upt-field-row">
                        <span className="upt-field-label">Notification Preferences</span>
                        <div style={{ marginTop: "4px" }}>
                            <div className="upt-notification-row">
                                <span className="upt-notification-label">Email Notifications</span>
                                {isEditing && !readOnly ? (
                                    <label className="upt-toggle">
                                        <input
                                            type="checkbox"
                                            checked={form.discrepancyNotification}
                                            onChange={(e) => handleChange("discrepancyNotification", e.target.checked as any)}
                                        />
                                        <span className="upt-toggle-slider" />
                                    </label>
                                ) : (
                                    <span className={`upt-notification-status ${form.discrepancyNotification ? "enabled" : "disabled"}`}>
                                        {form.discrepancyNotification ? "On" : "Off"}
                                    </span>
                                )}
                            </div>
                            <div className="upt-notification-row">
                                <span className="upt-notification-label">PM</span>
                                {isEditing && !readOnly ? (
                                    <label className="upt-toggle">
                                        <input
                                            type="checkbox"
                                            checked={form.pmNotification}
                                            onChange={(e) => handleChange("pmNotification", e.target.checked as any)}
                                        />
                                        <span className="upt-toggle-slider" />
                                    </label>
                                ) : (
                                    <span className={`upt-notification-status ${form.pmNotification ? "enabled" : "disabled"}`}>
                                        {form.pmNotification ? "On" : "Off"}
                                    </span>
                                )}
                            </div>
                            <div className="upt-notification-row">
                                <span className="upt-notification-label">Training</span>
                                {isEditing && !readOnly ? (
                                    <label className="upt-toggle">
                                        <input
                                            type="checkbox"
                                            checked={form.trainingNotification}
                                            onChange={(e) => handleChange("trainingNotification", e.target.checked as any)}
                                        />
                                        <span className="upt-toggle-slider" />
                                    </label>
                                ) : (
                                    <span className={`upt-notification-status ${form.trainingNotification ? "enabled" : "disabled"}`}>
                                        {form.trainingNotification ? "On" : "Off"}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
